import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { ref, remove } from 'firebase/database';
import { auth, db, realtimeDb } from '../config/firebase';

// Türkiye saatini API'den al (güvenilir sunucu saati)
const getTurkeyTime = async (): Promise<Date> => {
  try {
    const response = await fetch('https://worldtimeapi.org/api/timezone/Europe/Istanbul');
    const data = await response.json();
    return new Date(data.datetime);
  } catch {
    // API başarısız olursa Timestamp.now() kullan (fallback)
    return new Date();
  }
};

// Rol tipleri
export type UserRole = 'admin' | 'user' | 'live_support' | 'risk' | 'game_management' | 'promo_management' | 'marketing';

// Rol isimleri (Türkçe)
export const ROLE_NAMES: Record<UserRole, string> = {
  admin: 'Admin',
  user: 'Kullanıcı',
  live_support: 'Canlı Destek',
  risk: 'Risk',
  game_management: 'Game Management',
  promo_management: 'Promo Management',
  marketing: 'Marketing'
};

// Admin panel erişim türleri
export type AdminTabType = 'users' | 'chat' | 'support' | 'market' | 'banners' | 'promo' | 'tournaments' | 'games' | 'marketing' | 'risk' | 'sponsors' | 'bonusCodes' | 'messages' | 'applications' | 'pages' | 'backup';

// Her rolün erişebildiği tablar
export const ROLE_PERMISSIONS: Record<UserRole, AdminTabType[]> = {
  admin: ['users', 'chat', 'support', 'market', 'banners', 'promo', 'tournaments', 'games', 'marketing', 'risk', 'sponsors', 'bonusCodes', 'pages', 'backup'],
  user: [],
  live_support: ['chat', 'support'],
  risk: ['risk', 'users'],
  game_management: ['games'],
  promo_management: ['promo', 'tournaments', 'banners', 'market'],
  marketing: ['marketing']
};

// Rol erişim kontrolü
export const hasAccess = (role: UserRole, tab: AdminTabType): boolean => {
  if (role === 'admin') return true;
  return ROLE_PERMISSIONS[role]?.includes(tab) || false;
};

// Kullanıcının admin paneline erişip erişemeyeceği
export const canAccessAdminPanel = (role: UserRole): boolean => {
  return role !== 'user';
};

// Cihaz bilgisi tipi
export interface DeviceInfo {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: string;
  isMobile: boolean;
}

// Giriş geçmişi tipi
export interface LoginHistory {
  ip: string;
  device: DeviceInfo;
  timestamp: any;
}

// Kullanılmış bonus tipi
export interface UsedBonus {
  codeId: string;
  code: string;
  amount: number;
  usedAt: any;
}

interface UserData {
  uid: string;
  email: string;
  username: string;
  displayName?: string;
  bio?: string;
  location?: string;
  website?: string;
  discord?: string;
  steam?: string;
  github?: string;
  role: UserRole;
  isActive: boolean;
  createdAt: any;
  lastLogin: any;
  lastLoginIP: string;
  lastDevice?: DeviceInfo;
  loginHistory?: LoginHistory[];
  loginCount: number;
  balance?: number;
  usedBonuses?: UsedBonus[];
}

// Cihaz bilgisini algılayan yardımcı fonksiyon
export const getDeviceInfo = (): DeviceInfo => {
  const ua = navigator.userAgent;

  // Tarayıcı algılama
  let browser = 'Unknown';
  let browserVersion = '';

  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
    browser = 'Opera';
    browserVersion = ua.match(/(?:Opera|OPR)\/(\d+\.\d+)/)?.[1] || '';
  }

  // İşletim sistemi algılama
  let os = 'Unknown';
  let osVersion = '';

  if (ua.includes('Windows NT')) {
    os = 'Windows';
    const ntVersion = ua.match(/Windows NT (\d+\.\d+)/)?.[1];
    if (ntVersion === '10.0') osVersion = '10/11';
    else if (ntVersion === '6.3') osVersion = '8.1';
    else if (ntVersion === '6.2') osVersion = '8';
    else if (ntVersion === '6.1') osVersion = '7';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    osVersion = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
    osVersion = ua.match(/Android (\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    osVersion = ua.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || '';
  }

  // Cihaz türü algılama
  let device = 'Desktop';
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  if (ua.includes('iPad')) {
    device = 'iPad';
  } else if (ua.includes('iPhone')) {
    device = 'iPhone';
  } else if (ua.includes('Android')) {
    if (ua.includes('Mobile')) {
      device = 'Android Phone';
    } else {
      device = 'Android Tablet';
    }
  } else if (isMobile) {
    device = 'Mobile';
  }

  return {
    browser,
    browserVersion,
    os,
    osVersion,
    device,
    isMobile
  };
};

interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  loading: boolean;
  register: (email: string, password: string, username: string, userIP: string, deviceInfo: DeviceInfo) => Promise<void>;
  login: (email: string, password: string, userIP: string, deviceInfo: DeviceInfo) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profileData: Partial<UserData>) => Promise<void>;
  isAdmin: boolean;
  getAllUsers: () => Promise<UserData[]>;
  updateUserRole: (uid: string, role: UserRole) => Promise<void>;
  toggleUserStatus: (uid: string, isActive: boolean) => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);


  // Kullanıcı kaydı
  const register = async (email: string, password: string, username: string, userIP: string, deviceInfo: DeviceInfo) => {
    try {
      // Firebase Authentication ile kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // İlk giriş geçmişi kaydı (Türkiye saatini API'den alıyoruz)
      const turkeyTime = await getTurkeyTime();
      const firstLogin: LoginHistory = {
        ip: userIP,
        device: deviceInfo,
        timestamp: Timestamp.fromDate(turkeyTime)
      };

      // Firestore'da kullanıcı bilgilerini sakla
      const userData: UserData = {
        uid: user.uid,
        email: user.email || '',
        username: username,
        role: 'user',
        isActive: true,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        lastLoginIP: userIP,
        lastDevice: deviceInfo,
        loginHistory: [firstLogin],
        loginCount: 1,
        balance: 0
      };

      await setDoc(doc(db, 'users', user.uid), userData);
      setUserData(userData);

    } catch (error: any) {
      console.error('Register error:', error);

      // Firebase hata kodlarını Türkçe mesajlara çevir
      const errorCode = error.code;
      let message = 'Kayıt işlemi sırasında bir hata oluştu';

      if (errorCode === 'auth/email-already-in-use') {
        message = 'Bu e-posta adresi zaten kullanılıyor';
      } else if (errorCode === 'auth/invalid-email') {
        message = 'Geçersiz e-posta adresi';
      } else if (errorCode === 'auth/weak-password') {
        message = 'Şifre çok zayıf. En az 6 karakter kullanın';
      } else if (errorCode === 'auth/operation-not-allowed') {
        message = 'E-posta/şifre girişi devre dışı';
      } else if (errorCode === 'permission-denied') {
        message = 'Veritabanı izin hatası. Lütfen yönetici ile iletişime geçin';
      } else if (error.message) {
        // Diğer hatalar için orijinal mesajı kullan (geliştirme amaçlı)
        console.error('Unhandled error:', error.message);
      }

      throw new Error(message);
    }
  };

  // Kullanıcı girişi
  const login = async (email: string, password: string, userIP: string, deviceInfo: DeviceInfo) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Kullanıcı bilgilerini kontrol et
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();


        // Kullanıcı durumunu kontrol et
        if (userData.isActive === false) {
          // Kullanıcı pasif, çıkış yap
          await signOut(auth);
          throw new Error('Hesabınız deaktif edilmiştir. Lütfen yönetici ile iletişime geçin.');
        }

        // Yeni giriş kaydı (Türkiye saatini API'den alıyoruz)
        const turkeyTime = await getTurkeyTime();
        const newLogin: LoginHistory = {
          ip: userIP,
          device: deviceInfo,
          timestamp: Timestamp.fromDate(turkeyTime)
        };

        // Giriş geçmişini güncelle (son 10 giriş)
        const existingHistory = userData.loginHistory || [];
        const updatedHistory = [newLogin, ...existingHistory].slice(0, 10);

        const updatedData = {
          lastLogin: serverTimestamp(),
          lastLoginIP: userIP,
          lastDevice: deviceInfo,
          loginHistory: updatedHistory,
          loginCount: (userData.loginCount || 0) + 1
        };

        await setDoc(userRef, updatedData, { merge: true });

        // Güncellenmiş kullanıcı bilgilerini al
        const updatedSnap = await getDoc(userRef);
        setUserData({ ...updatedSnap.data(), uid: user.uid } as UserData);
      }

    } catch (error: any) {
      // Firebase hata kodlarını Türkçe mesajlara çevir
      const errorCode = error.code;
      let message = 'Giriş yapılırken bir hata oluştu';

      if (errorCode === 'auth/invalid-credential') {
        message = 'E-posta veya şifre hatalı';
      } else if (errorCode === 'auth/user-not-found') {
        message = 'Bu e-posta adresiyle kayıtlı kullanıcı bulunamadı';
      } else if (errorCode === 'auth/wrong-password') {
        message = 'Şifre hatalı';
      } else if (errorCode === 'auth/invalid-email') {
        message = 'Geçersiz e-posta adresi';
      } else if (errorCode === 'auth/user-disabled') {
        message = 'Bu hesap devre dışı bırakılmış';
      } else if (errorCode === 'auth/too-many-requests') {
        message = 'Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin';
      }

      throw new Error(message);
    }
  };

  // Kullanıcı çıkışı
  const logout = async () => {
    try {
      // Önce online status'u temizle
      if (currentUser) {
        const userStatusRef = ref(realtimeDb, `onlineUsers/${currentUser.uid}`);
        await remove(userStatusRef);
        
        // Ek güvenlik için bir kez daha temizle
        setTimeout(async () => {
          try {
            await remove(userStatusRef);
          } catch (e) {
            // Hata olursa görmezden gel
          }
        }, 1000);
      }
      
      await signOut(auth);
      setUserData(null);
    } catch (error: any) {
      console.error('Çıkış hatası:', error);
      throw new Error(error.message);
    }
  };

  // Profil güncelleme
  const updateProfile = async (profileData: Partial<UserData>) => {
    try {
      if (!currentUser) {
        throw new Error('Kullanıcı giriş yapmamış');
      }

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Sadece güncellenebilir alanları güncelle
      const updateData = {
        username: profileData.username,
        displayName: profileData.displayName,
        bio: profileData.bio,
        location: profileData.location,
        website: profileData.website,
        discord: profileData.discord,
        steam: profileData.steam,
        github: profileData.github,
        updatedAt: serverTimestamp()
      };

      // Boş değerleri temizle
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === '' || updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });

      await updateDoc(userRef, updateData);
      
      // Güncellenmiş kullanıcı bilgilerini al
      const updatedSnap = await getDoc(userRef);
      if (updatedSnap.exists()) {
        setUserData({ ...updatedSnap.data(), uid: currentUser.uid } as UserData);
      }
      
    } catch (error: any) {
      console.error('Profil güncelleme hatası:', error);
      throw new Error(error.message);
    }
  };

  // Admin kontrolü
  const isAdmin = userData?.role === 'admin';

  // Tüm kullanıcıları getir (sadece admin)
  const getAllUsers = async (): Promise<UserData[]> => {
    try {
      if (!isAdmin) {
        throw new Error('Bu işlem için admin yetkisi gereklidir');
      }

      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const users: UserData[] = [];
      querySnapshot.forEach((doc) => {
        users.push({ ...doc.data(), uid: doc.id } as UserData);
      });
      
      return users;
    } catch (error: any) {
      console.error('Kullanıcılar getirilemedi:', error);
      throw new Error(error.message);
    }
  };

  // Kullanıcı rolünü güncelle (sadece admin)
  const updateUserRole = async (uid: string, role: UserRole) => {
    try {
      if (!isAdmin) {
        throw new Error('Bu işlem için admin yetkisi gereklidir');
      }

      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, {
        role: role,
        updatedAt: serverTimestamp()
      }, { merge: true });

    } catch (error: any) {
      console.error('Rol güncelleme hatası:', error);
      throw new Error(error.message);
    }
  };

  // Kullanıcı durumunu değiştir (sadece admin)
  const toggleUserStatus = async (uid: string, isActive: boolean) => {
    try {
      // Admin kontrolü - daha güvenilir
      if (!currentUser || !userData) {
        throw new Error('Kullanıcı bilgileri yüklenmedi');
      }

      if (userData.role !== 'admin') {
        throw new Error('Bu işlem için admin yetkisi gereklidir');
      }


      const userRef = doc(db, 'users', uid);
      
      // Önce mevcut veriyi kontrol et
      await getDoc(userRef);
      
      await setDoc(userRef, {
        isActive: isActive,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      // Güncelleme sonrası kontrol et
      await getDoc(userRef);
    } catch (error: any) {
      console.error('Durum güncelleme hatası:', error);
      
      // Daha açıklayıcı hata mesajları
      if (error.code === 'permission-denied') {
        throw new Error('Bu işlem için yeterli yetkiniz yok. Lütfen admin olarak giriş yapın.');
      } else if (error.code === 'not-found') {
        throw new Error('Kullanıcı bulunamadı.');
      } else {
        throw new Error(`Durum güncellenemedi: ${error.message}`);
      }
    }
  };

  // Authentication state değişikliklerini dinle
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Kullanıcı giriş yapmış, Firestore'dan bilgileri al
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            
            // Kullanıcı durumunu kontrol et
            if (!userData.isActive) {
              // Kullanıcı pasif, çıkış yap
              await signOut(auth);
              setUserData(null);
              setCurrentUser(null);
              setLoading(false);
              return;
            }
            
            setUserData({ ...userData, uid: user.uid } as UserData);
          }
        } catch (error) {
          console.error('Kullanıcı bilgileri alınamadı:', error);
        }
      } else {
        // Kullanıcı çıkış yaptı, online status'u temizle
        setUserData(null);
        // Online status'u temizlemek için ChatContext'e güveniyoruz
        // Çünkü burada currentUser henüz null olmamış olabilir
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Kullanıcı verilerini yeniden yükle (bakiye güncellemesi için)
  const refreshUserData = async () => {
    if (!currentUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData({ ...data, uid: currentUser.uid } as UserData);
      }
    } catch (error) {
      console.error('Kullanıcı verileri yenilenemedi:', error);
    }
  };

  const value = {
    currentUser,
    userData,
    loading,
    register,
    login,
    logout,
    updateProfile,
    isAdmin,
    getAllUsers,
    updateUserRole,
    toggleUserStatus,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
